import { createContext, useContext, useEffect, useState } from 'react'

import { getPublicCompressed } from '@toruslabs/eccrypto'
import { AuthAdapter } from '@web3auth/auth-adapter'
import { IWeb3AuthCoreOptions, WALLET_ADAPTERS, WEB3AUTH_NETWORK, getEvmChainConfig } from '@web3auth/base'
import { EthereumPrivateKeyProvider } from '@web3auth/ethereum-provider'
import { Web3AuthNoModal } from '@web3auth/no-modal'

import { api } from 'lib/api/api'
import { getErrorMessage, getRedirecURL, getWeb3AuthErrorMessage } from 'lib/web3auth-utils'
import { usePathname } from 'next/navigation'

const clientId = process.env.NEXT_PUBLIC_WEB3AUTH_CLIENT_ID
const chainId = process.env.NEXT_PUBLIC_WEB3AUTH_CHAIN_ID

if (!clientId) throw new Error('NEXT_PUBLIC_WEB3AUTH_CLIENT_ID is not set')
if (!chainId) throw new Error('NEXT_PUBLIC_WEB3AUTH_CHAIN_ID is not set')

const chainConfig = getEvmChainConfig(parseInt(chainId), clientId)

if (!chainConfig) throw new Error('Invalid chain config')

const privateKeyProvider = new EthereumPrivateKeyProvider({
  config: {
    chainConfig
  }
})

const web3AuthOptions: IWeb3AuthCoreOptions = {
  chainConfig,
  clientId,
  privateKeyProvider,
  web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_DEVNET
}

const web3auth = new Web3AuthNoModal(web3AuthOptions)

const authadapter = new AuthAdapter()
web3auth.configureAdapter(authadapter)

export type LoginProvider =
  | 'google'
  | 'github'
  | 'twitter'
  | 'facebook'
  | 'discord'
  | 'line'
  | 'reddit'
  | 'apple'
  | 'twitch'
  | 'linkedin'
  | 'wechat'
  | 'kakao'

interface IWeb3Context {
  error: string | null
  submitting: boolean
  ready: boolean
  loggedIn: boolean
  loginProvider: LoginProvider | null
  authenticate: (csrfToken: string, provider: LoginProvider) => void
  logout: () => void
}

const Web3Contenxt = createContext<IWeb3Context>(null!)

export const Web3Provider = ({ children }: { children: React.ReactNode }) => {
  const [loginProvider, setLoginProvider] = useState<LoginProvider | null>(null)
  const [loggedIn, setLoggedIn] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  const pathname = usePathname()

  useEffect(() => {
    setError(null)
  }, [pathname])

  useEffect(() => {
    const init = async () => {
      try {
        await web3auth.init()
        setReady(true)
        if (web3auth.connected) {
          setLoggedIn(true)
        }
      } catch (error) {}
    }

    init()
  }, [])

  const getAppPubKey = async () => {
    const app_scoped_privkey = (await web3auth.provider?.request({
      method: 'eth_private_key' // use "private_key" for other non-evm chains
    })) as string

    return getPublicCompressed(Buffer.from(app_scoped_privkey.padStart(64, '0'), 'hex')).toString('hex')
  }

  const authenticate = async (csrfToken: string, loginProvider: LoginProvider) => {
    setSubmitting(true)
    setError(null)
    setLoginProvider(loginProvider)
    if (loggedIn) await web3auth.logout()

    try {
      await web3auth.connectTo(WALLET_ADAPTERS.AUTH, { loginProvider })
    } catch (e) {
      setError(getWeb3AuthErrorMessage(e))
      setSubmitting(false)
      setLoginProvider(null)
      return
    }

    if (web3auth.connected) {
      setLoggedIn(true)
    }

    try {
      const userInfo = await web3auth.getUserInfo()

      const appPubKey = await getAppPubKey()
      const response = await api.webThreeAuthAuthenticatePost(
        `Bearer ${userInfo.idToken}`,
        { appPubKey },
        {
          headers: {
            'Cache-Control': 'no-cache',
            csrf_token: csrfToken,
            Pragma: 'no-cache'
            // recaptcha_bypass: query.bypass,
          }
        }
      )

      const nextHref = response?.data?.session?.redirect_to ?? getRedirecURL()
      if (window.location.pathname !== nextHref) {
        window.location.href = nextHref
      }
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setLoginProvider(null)
      setSubmitting(false)
    }
  }

  const logout = async () => {
    await web3auth.logout()
    setLoginProvider(null)
    setLoggedIn(false)
  }

  return (
    <Web3Contenxt.Provider value={{ authenticate, error, loggedIn, loginProvider, logout, ready, submitting }}>
      {children}
    </Web3Contenxt.Provider>
  )
}

export const useWeb3Auth = () => {
  const context = useContext(Web3Contenxt)
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider')
  }
  return context
}
